import { useEffect, useMemo, useState } from "react";
import { Control, Controller, useForm, useWatch } from "react-hook-form";
import { UseFormSetValue } from "react-hook-form/dist/types/form";
import { useResizeDetector } from "react-resize-detector";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Check from "@mui/icons-material/Check";
import CloudDone from "@mui/icons-material/CloudDone";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  menuItemClasses,
  OutlinedInput,
  Select,
  selectClasses,
  Skeleton,
  Slider,
  sliderClasses,
  Stack,
  styled,
  Tooltip,
  Typography as T,
} from "@mui/material";
import prettyBytes from "pretty-bytes";
import {
  AUTO_STOP_IDLE_MINUTES_MAX,
  AUTO_STOP_IDLE_MINUTES_MIN,
} from "rwb/pages/project/constants/autoStopIdleMinutes";
import useComputePrices from "rwb/pages/project/hooks/useComputePrices";
import useExternalComputeProvider from "rwb/pages/project/hooks/useExternalComputeProvider";
import useInstanceTypes from "rwb/pages/project/hooks/useInstanceTypes";
import ImageType, {
  AUTO_STOP_SCOPED_IMAGE_TYPES,
  DISPLAY_COMPUTE_IMAGE,
} from "rwb/pages/project/types/enums/imageType";
import InstanceType, { DISPLAY_INSTANCE_TYPE } from "rwb/pages/project/types/enums/instanceType";
import InstanceTypeConfig from "rwb/pages/project/types/enums/instanceTypeConfig";
import yn from "yn";
import Switch from "~/components/Switch";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { extractErrorCode, extractErrorMessage } from "~/utils/apiClient";

const { VITE_RWB_API, VITE_AUTO_STOP_ENABLED } = import.meta.env;
const GIGABYTE = Math.pow(1024, 3);

const currencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const compactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export interface EnvironmentForm {
  name: string;
  image_type: ImageType | "";
  instance_type: InstanceType | "";
  volume_size: number;
  auto_stop_enabled: boolean;
  auto_stop_idle_minutes: number;
}

interface ImageTypeWithConstraints {
  image_type: ImageType;
  instance_type_constraints: InstanceType[];
}

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  "&:hover .code": {
    backgroundColor: theme.palette.grey[200],
  },
  [`&.${menuItemClasses.root}`]: {
    color: "initial",
    backgroundColor: "initial",
    "&:hover": { color: "initial", backgroundColor: "#F7F5FC" },
  },
}));

const InstanceTypeSelectOption = ({
  label,
  vcpu,
  memory,
  selected,
  instance_type,
}: {
  label: string;
  selected?: boolean;
} & InstanceTypeConfig) => {
  const { data: priceData, isLoading: loadingPrices } = useComputePrices();
  const instancePrice = priceData?.instance_types?.[instance_type];

  return (
    <Box
      display="flex"
      gap={2}
      alignItems="center"
      justifyContent="space-between"
      minHeight={30}
      width="100%"
      position="relative"
      pr={2}
    >
      <T variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 100 }}>
        {label}
      </T>
      {vcpu && memory && (
        <Box display="flex" gap={[0, 1]} flexDirection={["column", "row"]}>
          <T variant="body2" sx={{ textAlign: ["left", "right"], minWidth: 70 }}>
            {vcpu}{" "}
            <Box component="span" display="inline">
              vCPUs
            </Box>
          </T>
          <Divider orientation="vertical" flexItem />
          <T variant="body2" sx={{ minWidth: 110 }}>
            {memory}{" "}
            <Box component="span" display="inline">
              GiB Memory
            </Box>
          </T>
          {(instancePrice || loadingPrices) && (
            <>
              <Divider orientation="vertical" flexItem />

              {instancePrice !== undefined ? (
                <T variant="body2" sx={{ minWidth: 80 }}>
                  {currencyFormatter.format(instancePrice)}
                  <Box component="span" display="inline">
                    /hour
                  </Box>
                </T>
              ) : (
                <Skeleton sx={{ width: 70, mr: "10px" }} />
              )}
            </>
          )}
        </Box>
      )}
      {selected && <Check sx={{ position: "absolute", right: 3, fontSize: 16 }} />}
    </Box>
  );
};

const ImageSelectOption = ({ type, selected }: { type: ImageType; selected?: boolean }) => {
  const user = useUser();
  const { icon: Icon } = DISPLAY_COMPUTE_IMAGE[type];
  const { label, description } = useMemo(() => {
    const overrides = user?.app_metadata?.workbench?.image_type_overrides?.[type] ?? {};
    return { ...DISPLAY_COMPUTE_IMAGE[type], ...overrides };
  }, [user, type]);

  return (
    <Box display="flex" gap={2} alignItems="center" minHeight={60} width="100%" py={1}>
      <Icon width={44} height={44} />
      <Stack whiteSpace="normal" flex={1}>
        <T variant="body2" fontWeight={500} mb={0.5}>
          {label}
        </T>
        {description}
      </Stack>
      <Box width={24} display="flex" alignItems="center" justifyContent="center">
        {selected && <Check />}
      </Box>
    </Box>
  );
};

export const AdvancedOptions = ({
  formControl,
  setValue,
  imageTypeConstraints,
}: {
  formControl: Control<EnvironmentForm>;
  setValue: UseFormSetValue<EnvironmentForm>;
  imageTypeConstraints?: InstanceType[];
}) => {
  const instanceTypeWatch = useWatch({
    control: formControl,
    name: "instance_type",
  }) as InstanceType;
  const { width: selectWidth, ref: selectRef } = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 300,
  });

  const { data: instanceTypeData, isLoading: loadingInstanceOptions } = useInstanceTypes();

  const { data: priceData, isLoading: loadingPrices } = useComputePrices();

  const options = useMemo(() => {
    if (instanceTypeData) {
      // Combine available type specs (backend) with display labels (frontend)
      const allOptions = instanceTypeData.reduce(
        (acc, { instance_type, vcpu, memory, volume_size_options }) => {
          const displayConfig = DISPLAY_INSTANCE_TYPE[instance_type];

          return {
            ...acc,
            [instance_type]: {
              // Default to backend name if no label configured
              label: displayConfig ? displayConfig.label : instance_type,
              vcpu,
              memory,
              volume_size_options,
              instance_type,
            },
          };
        },
        {}
      ) as Record<InstanceType, InstanceTypeConfig>;

      // Filter by image type constraints if they exist
      if (imageTypeConstraints && imageTypeConstraints.length > 0) {
        return Object.fromEntries(
          Object.entries(allOptions).filter(([instanceType]) =>
            imageTypeConstraints.includes(instanceType as InstanceType)
          )
        ) as Record<InstanceType, InstanceTypeConfig>;
      }

      return allOptions;
    }
  }, [instanceTypeData, imageTypeConstraints]);

  const storageOptions = options?.[instanceTypeWatch]?.volume_size_options ?? [];
  const minStorageSize = storageOptions[0] ?? 0;
  const maxStorageSize = storageOptions.at(-1) ?? 0;
  // The ladder is not uniformly spaced (a 50 GB floor, then 100 GB steps), so the
  // slider has to be driven by the offered values rather than a single step size.
  const storageMarks = storageOptions.map((value) => ({ value }));

  useEffect(() => {
    if (options && Object.keys(options).length > 0) {
      // Asynchronously set instance type and volume size after loading configurations from backend
      const firstInstanceType = Object.keys(options)[0] as InstanceType;
      const firstOption = options[firstInstanceType];

      setValue("instance_type", firstInstanceType, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });

      setValue("volume_size", firstOption.volume_size_options[0], {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [options, setValue]);

  useEffect(() => {
    if (storageOptions) {
      // Change volume size to the default for new instance type on change
      setValue("volume_size", storageOptions[0]);
    }
  }, [instanceTypeWatch, storageOptions, setValue]);

  return (
    <Stack py={3} pb={3}>
      <Stack gap={3}>
        <FormControl>
          <FormLabel htmlFor="instance_type">
            <T variant="label">Environment Size</T>
          </FormLabel>
          <Controller
            control={formControl}
            name="instance_type"
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                id={field.name}
                displayEmpty
                renderValue={() => {
                  if (loadingInstanceOptions) {
                    return (
                      <Box display="flex" gap={2} height={30} mr={1}>
                        <Skeleton sx={{ width: 100 }} />
                        <Skeleton sx={{ width: 225, ml: "auto" }} />
                      </Box>
                    );
                  }

                  if (options && instanceTypeWatch) {
                    return <InstanceTypeSelectOption {...options[instanceTypeWatch]} />;
                  }
                }}
                sx={{ [`.${selectClasses.icon}`]: { right: 16 } }}
                MenuProps={{ sx: { width: selectWidth } }}
                {...field}
                ref={selectRef}
              >
                {options &&
                  Object.entries(options).map(([type, specs]) => (
                    <StyledMenuItem key={type} value={type}>
                      <InstanceTypeSelectOption selected={field.value === type} {...specs} />
                    </StyledMenuItem>
                  ))}
              </Select>
            )}
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="volume_size">
            <T variant="label">Storage Size</T>
          </FormLabel>
          <Box maxWidth={800} px={1}>
            <Controller
              control={formControl}
              name="volume_size"
              rules={{ required: true }}
              render={({ field: { onChange, value, name } }) => {
                const storagePrice = priceData?.volume_sizes?.[value];
                return (
                  <Box display="flex" alignItems="center">
                    <Slider
                      marks={storageMarks}
                      name={name}
                      value={value}
                      onChange={onChange}
                      getAriaValueText={(value) => `${value} gigabytes`}
                      min={minStorageSize}
                      max={maxStorageSize}
                      step={null}
                      sx={{
                        flex: "1 1 auto",
                        [`.${sliderClasses.rail}`]: {
                          color: "rgba(0, 0, 0, 0.23)",
                        },
                        [`.${sliderClasses.track}`]: {
                          opacity: 0.57,
                        },
                      }}
                    />
                    <Box minWidth={110} textAlign="right">
                      {(value || loadingInstanceOptions) && (
                        <T variant="body2">
                          {value ? (
                            prettyBytes(value * GIGABYTE, { binary: true })
                          ) : (
                            <Skeleton sx={{ width: 50, ml: "auto" }} />
                          )}
                        </T>
                      )}
                      {(loadingPrices || storagePrice) && (
                        <T variant="body2" sx={{ opacity: 0.7 }}>
                          {storagePrice ? (
                            `${compactCurrencyFormatter.format(storagePrice)}/month`
                          ) : (
                            <Skeleton sx={{ width: 60, ml: "auto" }} />
                          )}
                        </T>
                      )}
                    </Box>
                  </Box>
                );
              }}
            />
          </Box>
        </FormControl>
      </Stack>
    </Stack>
  );
};

export const AutoStopOptions = ({ formControl }: { formControl: Control<EnvironmentForm> }) => {
  const imageTypeWatch = useWatch({ control: formControl, name: "image_type" });
  const autoStopEnabledWatch = useWatch({ control: formControl, name: "auto_stop_enabled" });

  // Kill switch: auto-stop is scoped to manifold-stage only (VITE_AUTO_STOP_ENABLED),
  // pending detecting terminal activity (an actively-used terminal session
  // still gets auto-stopped today). Remove this early return once that's fixed.
  if (!yn(VITE_AUTO_STOP_ENABLED, { default: false })) {
    return null;
  }

  if (
    AUTO_STOP_SCOPED_IMAGE_TYPES.length > 0 &&
    !AUTO_STOP_SCOPED_IMAGE_TYPES.includes(imageTypeWatch as ImageType)
  ) {
    return null;
  }

  return (
    <>
      <Divider />
      <Stack py={3} gap={2}>
        <FormControl>
          <FormControlLabel
            sx={{ ml: 0, gap: 1 }}
            control={
              <Controller
                control={formControl}
                name="auto_stop_enabled"
                render={({ field: { value, onChange, ...field } }) => (
                  <Switch checked={value} onChange={(e) => onChange(e.target.checked)} {...field} />
                )}
              />
            }
            labelPlacement="start"
            label={<T variant="label">Enable auto-stop when idle</T>}
          />
        </FormControl>
        {autoStopEnabledWatch && (
          <FormControl>
            <FormLabel htmlFor="auto_stop_idle_minutes">
              <T variant="label">Idle timeout duration</T>
            </FormLabel>
            <Controller
              control={formControl}
              name="auto_stop_idle_minutes"
              rules={{
                required: { value: true, message: "Required" },
                validate: (value) => Number.isInteger(value) || "Must be a whole number",
                min: {
                  value: AUTO_STOP_IDLE_MINUTES_MIN,
                  message: `Must be at least ${AUTO_STOP_IDLE_MINUTES_MIN} minute`,
                },
                max: {
                  value: AUTO_STOP_IDLE_MINUTES_MAX,
                  message: `Must be at most ${AUTO_STOP_IDLE_MINUTES_MAX} minutes`,
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Box display="flex" alignItems="center" gap={1}>
                    <OutlinedInput
                      id={field.name}
                      size="small"
                      type="number"
                      inputProps={{ step: "any" }}
                      error={!!error}
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                    <T variant="body2" sx={{ opacity: 0.7 }}>
                      minutes
                    </T>
                  </Box>
                  <FormHelperText error>{error?.message ?? " "}</FormHelperText>
                </>
              )}
            />
          </FormControl>
        )}
      </Stack>
    </>
  );
};

const SelectImageType = ({
  formControl,
  setValue,
  onImageTypeConstraintsChange,
}: {
  formControl: Control<EnvironmentForm>;
  setValue: UseFormSetValue<EnvironmentForm>;
  onImageTypeConstraintsChange: (constraints: InstanceType[]) => void;
}) => {
  const user = useUser();
  const imageTypeWatch = useWatch({ control: formControl, name: "image_type" });
  const { data, isLoading } = useFetcher<ImageTypeWithConstraints[]>(
    `${VITE_RWB_API}/resources/image-types?instance_constraints=true`
  );
  const { width: selectWidth, ref: selectRef } = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 300,
  });

  const options: ImageType[] = useMemo(() => {
    if (user && data) {
      const overrides = user.app_metadata?.workbench?.image_type_overrides;
      return data
        .filter((item) => {
          if (overrides) {
            const override = overrides[item.image_type];
            if (override) {
              // Handle user specific overrides used to hide image from options
              return !override.hidden;
            }
          }
          // Default to showing if no overrides present for image type
          return true;
        })
        .map((item) => item.image_type);
    }
    return [];
  }, [user, data]);

  useEffect(() => {
    if (options?.length) {
      // Asynchronously set image type after loading configurations from backend
      setValue("image_type", options[0], {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [options, setValue]);

  useEffect(() => {
    if (imageTypeWatch && data) {
      // Notify parent about the instance type constraints for the selected image type
      const selectedImageData = data.find((item) => item.image_type === imageTypeWatch);
      if (selectedImageData) {
        onImageTypeConstraintsChange(selectedImageData.instance_type_constraints);
      }
    }
  }, [imageTypeWatch, data, onImageTypeConstraintsChange]);

  const selectedCaption = imageTypeWatch ? DISPLAY_COMPUTE_IMAGE[imageTypeWatch]?.caption : null;

  return (
    <FormControl>
      <FormLabel htmlFor="image_type">
        <T variant="label">Image</T>
      </FormLabel>
      <Controller
        control={formControl}
        name="image_type"
        rules={{ required: true }}
        render={({ field }) => (
          <Select
            id={field.name}
            displayEmpty
            renderValue={() => {
              if (isLoading) {
                return (
                  <Box display="flex" gap={2} alignItems="center">
                    <Skeleton variant="rounded" width={44} height={44} />
                    <Stack flex={1} my={0.75}>
                      <Skeleton variant="text" sx={{ width: 100, mb: 1 }} />
                      <Skeleton variant="text" sx={{ width: "75%" }} />
                      <Skeleton variant="text" sx={{ width: "85%" }} />
                      <Skeleton variant="text" sx={{ width: "80%" }} />
                    </Stack>
                  </Box>
                );
              }
              if (imageTypeWatch) {
                return <ImageSelectOption type={imageTypeWatch} />;
              }
            }}
            sx={{ [`.${selectClasses.icon}`]: { right: 16 } }}
            MenuProps={{ sx: { width: selectWidth } }}
            {...field}
            ref={selectRef}
          >
            {options?.map((type: ImageType) => (
              <StyledMenuItem key={type} value={type}>
                <ImageSelectOption type={type} selected={field.value === type} />
              </StyledMenuItem>
            ))}
          </Select>
        )}
      />
      {selectedCaption}
    </FormControl>
  );
};

// Match on the backend's stable `error.code` discriminator, not just the status code --
// a bare 503 can also come from an unrelated proxy/gateway and shouldn't have its
// message shown to the user. extractErrorCode/extractErrorMessage already guard
// against a missing or non-object body.
export function getCapacityErrorMessage(errorBody: unknown, fallback: string): string {
  if (extractErrorCode(errorBody) === "insufficient_capacity") {
    return extractErrorMessage(errorBody) ?? fallback;
  }
  return fallback;
}

export function getCreateEnvironmentErrorMessage(status: number, errorBody: unknown): string {
  if (
    status === 403 &&
    extractErrorMessage(errorBody) === "Funding source not available for this user"
  ) {
    return "You don't have access to the funding source for this project. Please contact your administrator to request access.";
  }
  return getCapacityErrorMessage(errorBody, "Unable to create compute environment");
}

const CreateEnvironmentForm = (props: {
  onClose?: () => void;
  refreshEnvs: () => Promise<void>;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [imageTypeConstraints, setImageTypeConstraints] = useState<InstanceType[]>([]);
  const { projectId } = useParams();
  const externalCompute = useExternalComputeProvider();

  const { control, setValue, formState, handleSubmit } = useForm<EnvironmentForm>({
    defaultValues: {
      name: "",
      image_type: "",
      instance_type: "",
      volume_size: 0,
      // Kill switch: matches the AutoStopOptions gate above -- only defaults
      // to enabled where the feature is actually scoped (manifold-stage).
      auto_stop_enabled: yn(VITE_AUTO_STOP_ENABLED, { default: false }),
      auto_stop_idle_minutes: 30,
    },
  });

  const onSubmit = async (data: EnvironmentForm) => {
    setError("");
    const environmentsUrl = `${VITE_RWB_API}/environments`;
    const resp = await fetch(environmentsUrl, {
      method: "POST",
      body: JSON.stringify({ ...data, project_id: projectId }),
      headers: { "Content-Type": "application/json" },
    });

    if (resp.ok) {
      await props.refreshEnvs();

      if (props.onClose) {
        props.onClose();
      }
    } else {
      const errorBody = await resp.json().catch(() => ({}));
      setError(getCreateEnvironmentErrorMessage(resp.status, errorBody));
    }

    if (location.state?.createNew) {
      // Remove createNew state if present to reveal env list
      navigate(location.pathname, { replace: true });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <FormControl>
          <FormLabel htmlFor="name">
            <T variant="label">Title *</T>
          </FormLabel>
          <Controller
            control={control}
            name="name"
            rules={{
              required: { value: true, message: "Required" },
              maxLength: { value: 255, message: "Name can't exceed 255 characters" },
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <OutlinedInput
                  id={field.name}
                  size="small"
                  autoFocus
                  placeholder="Descriptive title for this environment"
                  error={!!error}
                  {...field}
                  inputProps={{ "data-1p-ignore": true }} // disable 1Password
                />
                <FormHelperText error>{error?.message ?? " "}</FormHelperText>
              </>
            )}
          />
        </FormControl>
        <SelectImageType
          formControl={control}
          setValue={setValue}
          onImageTypeConstraintsChange={setImageTypeConstraints}
        />
        <AdvancedOptions
          formControl={control}
          setValue={setValue}
          imageTypeConstraints={imageTypeConstraints}
        />
        <AutoStopOptions formControl={control} />
        <Divider />
        <Box display="flex" mt={3} mb={1} alignItems="center" gap={1.5}>
          <CloudDone sx={{ color: "status.running", fontSize: 24 }} />
          <T variant="caption" sx={{ opacity: 0.6 }}>
            Automatically connected to Manifold
          </T>
        </Box>
        {externalCompute && (
          <T
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 1,
            }}
          >
            <Box display="flex" mr={1}>
              <externalCompute.icon width={24} />
            </Box>
            <Box component="span" sx={{ opacity: 0.6 }}>
              Provisioned on {externalCompute.owner} cloud account
            </Box>
            <Tooltip
              title={
                <Stack>
                  <Box>Cloud Provider: {externalCompute.provider}</Box>
                  <Box>Account: {externalCompute.id}</Box>
                  <Box>Region: {externalCompute.region}</Box>
                </Stack>
              }
            >
              <InfoOutlined sx={{ opacity: 0.5, fontSize: 16, "&:hover": { cursor: "pointer" } }} />
            </Tooltip>
          </T>
        )}
      </Stack>

      <Box display="flex" width="100%" justifyContent="flex-end" gap={1} mt={4}>
        {props.onClose && (
          <Button variant="outlined" onClick={props.onClose}>
            Cancel
          </Button>
        )}
        <LoadingButton
          type="submit"
          disabled={!formState.isValid}
          loading={formState.isSubmitting}
          variant="contained"
        >
          Add & Launch
        </LoadingButton>
      </Box>
    </form>
  );
};

export default CreateEnvironmentForm;
